import { useEffect, useMemo, useRef, useState } from "react";

import {
  cancelRestAlert,
  getUnreadComments,
  markCommentRead,
  scheduleRestAlert,
  type Exercise,
  type NextSet,
  type PlanItem,
  type SetKind,
  type UnreadComment,
} from "../lib/api";
import type { WorkoutFacts } from "../lib/storySlides";
import { plural } from "../lib/format";
import {
  beatsRecord,
  buildRows,
  currentRow,
  keyOf,
  pendingRows,
  planRemoval,
  plannedKinds,
  recordRow,
  reserveLabel,
  RPE_SCALE,
  rpeTone,
  setsOf,
  shiftDrafts,
  undoSaves,
  WARMUP_BAND,
  type Draft,
  type Row,
} from "../lib/setRows";
import { haptic, notify, onTelegramEvent, useNativeBack } from "../lib/telegram";
import { barFor, warmupFor, type WarmupSet } from "../lib/weights";
import { groupChained } from "../lib/programPlan";
import { useSession } from "../store/session";
import BrandBlock from "../components/BrandBlock";
import ConfirmButton from "../components/ConfirmButton";
import ExerciseIcon from "../components/ExerciseIcon";
import ExercisePickSheet from "../components/ExercisePickSheet";
import Numpad from "../components/Numpad";
import CalcSheet from "../components/CalcSheet";
import RestTimer, { fmtRest, useRestTimer } from "../components/RestTimer";
import Sheet from "../components/Sheet";
import UndoToast from "../components/UndoToast";
import DotLoader, { BusyLabel } from "../components/DotLoader";

interface Props {
  onLeave: () => void;
  /**
   * Итог отдаём наверх: этот экран к моменту показа уже размонтирован, а
   * `finish()` к тому моменту уже стёр `active` — считать надо ДО вызова.
   */
  onFinished: (facts: WorkoutFacts, clientId: string) => void;
}

/** «60 кг × 8», «× 12», «40 кг» — цель подхода одной строкой для напоминания бота. */
export function targetLine(weight: number | null, reps: number | null): string | null {
  if (weight !== null && reps) return `${fmtKg(weight)} кг × ${reps}`;
  if (reps) return `× ${reps}`;
  if (weight !== null) return `${fmtKg(weight)} кг`;
  return null;
}

const DEFAULT_REST_SEC = 120;
const REST_OPTIONS = [0, 30, 60, 90, 120, 150, 180, 240, 300];

/**
 * Отдых по упражнению запоминается на устройстве: на жиме нужно три минуты, на
 * разгибаниях — ноль, и выставлять это заново каждую тренировку никто не станет.
 * Это удобство одного телефона, а не данные тренировки, поэтому не сервер.
 */
const REST_KEY = "rack.rest-by-exercise";

function loadRestPrefs(): Record<number, number> {
  try {
    return JSON.parse(localStorage.getItem(REST_KEY) ?? "{}") as Record<number, number>;
  } catch {
    return {};
  }
}

/** 82.5 → «82.5», 80 → «80». Хвостовой ноль в зале только мешает. */
export const fmtKg = (v: number | null): string =>
  v === null ? "" : String(Number(v.toFixed(2)));

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Часы появляются только после первого часа: «00:12:05» — лишний шум. */
const clock = (sec: number) => {
  const h = Math.floor(sec / 3600);
  const mmss = `${pad2(Math.floor((sec % 3600) / 60))}:${pad2(sec % 60)}`;
  return h > 0 ? `${pad2(h)}:${mmss}` : mmss;
};

const restLabel = (sec: number) =>
  sec === 0 ? "ВЫКЛ" : `${Math.floor(sec / 60)}:${pad2(sec % 60)}`;

/** Вернуть ключ записи к прежнему значению, включая «ключа не было». */
function restoreKey<T>(record: Record<string, T>, key: string, value: T | undefined) {
  const next = { ...record };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}


export default function Session({ onLeave, onFinished }: Props) {
  const { active, pending, error, saveSet, removeSet, swap, addExtra, finish, discard, sync } =
    useSession();
  const [editing, setEditing] = useState<{
    item: PlanItem;
    row: Row;
    field: "weight" | "reps";
    /** Ввод начат с веса незасчитанного подхода: повторы откроются следом. */
    chained: boolean;
  } | null>(null);
  const [calculator, setCalculator] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [finishCheck, setFinishCheck] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [menuFor, setMenuFor] = useState<PlanItem | null>(null);
  const [restFor, setRestFor] = useState<PlanItem | null>(null);
  const [rpeFor, setRpeFor] = useState<{ item: PlanItem; row: Row } | null>(null);
  const [swapping, setSwapping] = useState<PlanItem | null>(null);
  const [adding, setAdding] = useState(false);
  // Предложенная разминка: план, а не записанные подходы. Ключ — пункт плана.
  const [warmups, setWarmups] = useState<Record<string, WarmupSet[]>>({});
  // Раскладка рабочих строк, изменённая в зале: добавленный подход или
  // дропсет, удалённая строка. Живёт в состоянии экрана: записанный подход и
  // так сохранится сам, а пустая строка — намерение, которому незачем
  // переживать перезагрузку.
  const [layouts, setLayouts] = useState<Record<string, SetKind[]>>({});
  // Значения, введённые до засчёта подхода. В сеть они не идут: подхода ещё нет.
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [restPrefs, setRestPrefs] = useState<Record<number, number>>(loadRestPrefs);
  // Непрочитанные комментарии тренера. Место им здесь, а не в отдельной
  // вкладке: замечание про технику нужно перед подходом, а не после.
  const [notes, setNotes] = useState<UnreadComment[]>([]);

  useEffect(() => {
    getUnreadComments()
      .then(setNotes)
      .catch(() => setNotes([]));
  }, []);

  async function dismiss(id: number) {
    haptic();
    setNotes((list) => list.filter((note) => note.id !== id));
    // Не дождались ответа — не беда: комментарий вернётся при следующем входе.
    await markCommentRead(id).catch(() => undefined);
  }
  const restTimer = useRestTimer();
  // Только что засчитанный подход, «пункт:строка»: ему короткая анимация заливки.
  const [justDone, setJustDone] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ id: number; text: string; run: () => void } | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef<number>(Date.now());
  // Засчитан подход — после перерисовки докручиваем до следующего.
  const followCurrent = useRef(false);
  // Набранное в numpad, но не подтверждённое «Далее». Раньше при переходе к
  // другому полю оно слетало, и вес приходилось набирать заново. Объявлено до
  // раннего return: хук ниже него менял бы число хуков между рендерами.
  const typed = useRef<number | null>(null);
  // Что сказать боту, если окно уйдёт в фон посреди отдыха. Ref, а не
  // зависимость эффекта: подписка на скрытие не должна пересоздаваться каждый тик.
  const restInfo = useRef<{ left: number; next: NextSet | null } | null>(null);

  useEffect(() => useNativeBack(onLeave), [onLeave]);

  useEffect(() => {
    if (!active) return;
    startedAt.current = new Date(active.started_at).getTime();
    const tick = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAt.current) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);

  // Пока экран открыт, очередь разбирается в фоне: связь в зале возвращается
  // сама, и заставлять человека что-то нажимать ради этого незачем.
  // На событие «сеть вернулась» подписан App — оно не про этот экран.
  useEffect(() => {
    const id = setInterval(() => void sync(), 20000);
    return () => clearInterval(id);
  }, [sync]);

  // «nearest», а не «center»: если следующий подход и так на экране, список не
  // должен дёргаться под пальцем.
  useEffect(() => {
    if (!followCurrent.current) return;
    followCurrent.current = false;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document
      .querySelector(".wo-set--current")
      ?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "nearest" });
  }, [active?.sets]);

  useEffect(() => {
    if (!justDone) return;
    const timer = setTimeout(() => setJustDone(null), 450);
    return () => clearTimeout(timer);
  }, [justDone]);

  // «Отдых окончен» от бота — только когда приложение ушло в фон: пока оно на
  // экране, отсчёт и вибрация есть и так, а сообщение на каждый подход засорило бы чат.
  useEffect(() => {
    const id = active?.client_id;
    if (!id) return;
    // Сообщение, оставшееся с прошлого раза, когда приложение закрыли совсем.
    cancelRestAlert(id).catch(() => {});
    let scheduled = false;
    const hide = () => {
      const info = restInfo.current;
      if (scheduled || !info || info.left < 5) return;
      scheduled = true;
      scheduleRestAlert(id, info.left, info.next).catch(() => {
        scheduled = false;
      });
    };
    const show = () => {
      if (!scheduled) return;
      scheduled = false;
      // Заодно удаляет уже отправленное: человек вернулся, оно своё сделало.
      cancelRestAlert(id).catch(() => {});
    };
    const onVisibility = () => (document.hidden ? hide() : show());
    document.addEventListener("visibilitychange", onVisibility);
    // Свёрнутый мини-апп WebView не всегда считает скрытой страницей — у
    // Telegram на это свои события. Двойной вызов гасит флаг scheduled.
    const offHide = onTelegramEvent("deactivated", hide);
    const offShow = onTelegramEvent("activated", show);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      offHide();
      offShow();
    };
  }, [active?.client_id]);

  const groups = useMemo(() => groupChained(active?.plan ?? []), [active?.plan]);

  if (!active) return <SessionSkeleton />;

  const rowsOf = (item: PlanItem): Row[] => {
    const key = keyOf(item);
    return buildRows(
      item,
      active.sets,
      warmups[key] ?? [],
      layouts[key] ?? plannedKinds(item),
    ).map((row) => (row.done ? row : { ...row, ...drafts[`${key}:${row.index}`] }));
  };

  const lists = active.plan.map((item) => ({ item, rows: rowsOf(item) }));
  const rowsByKey = new Map(lists.map((l) => [keyOf(l.item), l.rows]));
  const current = currentRow(lists.map((l) => l.rows));
  const currentKey = current ? keyOf(lists[current.list].item) : null;
  const pendingCount = lists.reduce((n, l) => n + pendingRows(l.rows).length, 0);
  // Прогресс — без разминки: её пропускают, и полоса не должна за это наказывать.
  const workRows = lists.flatMap((l) => l.rows.filter((r) => r.kind !== "warmup"));
  const doneCount = workRows.filter((r) => r.done).length;
  // Следующий подход словами — для напоминания бота.
  const next: NextSet | null = (() => {
    if (!current) return null;
    const { item, rows } = lists[current.list];
    const row = rows.find((r) => r.index === current.index);
    if (!row) return null;
    const work = rows.filter((r) => r.kind !== "warmup");
    return {
      exercise: item.exercise.name_ru,
      label: row.kind === "warmup" ? "Разминка" : `Подход ${work.indexOf(row) + 1} из ${work.length}`,
      target: targetLine(row.weight, row.reps),
    };
  })();
  restInfo.current = restTimer.rest ? { left: restTimer.rest.left, next } : null;

  const restOf = (item: PlanItem): number =>
    restPrefs[item.exercise.id] ?? item.rest_sec ?? DEFAULT_REST_SEC;

  const warmupOf = (item: PlanItem): WarmupSet[] =>
    item.recommendation.weight_kg === null
      ? []
      : // Ступени ведут от грифа ЭТОГО снаряда: на изогнутом первая ступень
        // 10 кг, а для гантелей ступени «пустой гриф» не существует вовсе.
        warmupFor(item.recommendation.weight_kg, barFor(item.exercise.equipment));

  const volume = active.sets.reduce(
    (sum, s) => sum + (s.weight_kg ?? 0) * (s.reps ?? 0),
    0,
  );

  const draftOf = (item: PlanItem, row: Row, index = row.index) => ({
    exercise_id: item.exercise.id,
    program_exercise_id: item.program_exercise_id,
    order_index: index,
    weight_kg: row.weight,
    reps: row.reps,
    rpe: row.rpe,
    kind: row.kind,
  });

  const offerUndo = (text: string, run: () => void) =>
    setUndo({ id: Date.now(), text, run });

  function toggle(item: PlanItem, row: Row) {
    if (row.done && row.setId) {
      haptic("light");
      const setId = row.setId;
      void removeSet(setId);
      offerUndo("Отметка снята", () => void saveSet(setId, draftOf(item, row)));
      return;
    }
    // Рекорд веса — отклик «успех», а не обычный тап: его чувствуешь рукой, не глядя.
    if (beatsRecord(rowsByKey.get(keyOf(item)) ?? [], row, item.record_weight_kg)) notify("success");
    else haptic("medium");
    followCurrent.current = true;
    setJustDone(`${keyOf(item)}:${row.index}`);
    void saveSet(null, { ...draftOf(item, row), rpe: null });
    // Сбросы дропсета идут подряд без отдыха, разминка отдыха не требует.
    const seconds = restOf(item);
    if (row.kind === "working" && seconds > 0) restTimer.start(seconds);
  }

  /** Записать набранное в открытое поле, не переходя к следующему. */
  function commitTyped(): { key: string; patch: { weight?: number; reps?: number } } | null {
    const value = typed.current;
    typed.current = null;
    if (!editing || value === null) return null;
    const { item, row, field } = editing;
    if (value === (field === "weight" ? row.weight : row.reps)) return null;
    const patch = field === "weight" ? { weight: value } : { reps: value };
    const key = `${keyOf(item)}:${row.index}`;
    if (row.setId) void saveSet(row.setId, draftOf(item, { ...row, ...patch }));
    else setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    return { key, patch };
  }

  /** Строка из разметки ещё не знает о только что записанном — подмешиваем. */
  const withCommitted = (
    item: PlanItem,
    row: Row,
    done: ReturnType<typeof commitTyped>,
  ): Row => (done && done.key === `${keyOf(item)}:${row.index}` ? { ...row, ...done.patch } : row);

  function openField(item: PlanItem, row: Row, field: "weight" | "reps") {
    const done = commitTyped();
    setEditing({ item, row: withCommitted(item, row, done), field, chained: false });
  }

  function toggleTyped(item: PlanItem, row: Row) {
    const done = commitTyped();
    if (editing) setEditing(null);
    toggle(item, withCommitted(item, row, done));
  }

  function applyValue(value: number) {
    if (!editing) return;
    typed.current = null;
    const { item, row, field, chained } = editing;
    const patch = field === "weight" ? { weight: value } : { reps: value };
    const next = { ...row, ...patch };
    setEditing(null);

    if (row.setId) {
      void saveSet(row.setId, draftOf(item, next));
      return;
    }
    // Правка незаписанного подхода сама его не засчитывает: пока человек
    // подбирает вес, подход ещё не сделан.
    const key = `${keyOf(item)}:${row.index}`;
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

    // Вес → повторы → засчитать: три ввода подряд без возврата пальца к
    // таблице. Раньше на один изменённый подход уходило семь тапов.
    if (field === "weight") {
      setEditing({ item, row: next, field: "reps", chained: true });
    } else if (chained) {
      toggle(item, next);
    }
  }

  /** Тап по «Пред.»: повторить прошлый раз — самый частый ввод в зале. */
  function copyPrevious(item: PlanItem, row: Row) {
    if (row.done || !row.previous?.weight_kg) return;
    haptic("light");
    const key = `${keyOf(item)}:${row.index}`;
    const { weight_kg, reps } = row.previous;
    setDrafts((prev) => ({
      ...prev,
      [key]: { weight: weight_kg, ...(reps !== null ? { reps } : {}) },
    }));
  }

  function addRow(item: PlanItem, kind: SetKind) {
    haptic();
    // Раскладку снимаем с того, что на экране: там уже учтены записанные
    // подходы сверх программы.
    const current = (rowsByKey.get(keyOf(item)) ?? [])
      .filter((r) => r.index < WARMUP_BAND)
      .map((r) => r.kind);
    setLayouts((prev) => ({ ...prev, [keyOf(item)]: [...current, kind] }));
  }

  function removeRow(item: PlanItem, row: Row) {
    haptic("light");
    const key = keyOf(item);
    const removal = planRemoval(rowsByKey.get(key) ?? [], row, warmups[key]?.length ?? 0);
    const before = { layout: layouts[key], warmup: warmups[key], drafts };

    if (removal.deleteId) void removeSet(removal.deleteId);
    for (const moved of removal.moves) {
      void saveSet(moved.setId, draftOf(item, moved, moved.index - 1));
    }
    const { kinds, warmupAt } = removal;
    if (kinds) setLayouts((prev) => ({ ...prev, [key]: kinds }));
    if (warmupAt !== null) {
      setWarmups((prev) => ({
        ...prev,
        [key]: (prev[key] ?? []).filter((_, i) => i !== warmupAt),
      }));
    }
    setDrafts((prev) => shiftDrafts(prev, key, row.index));

    offerUndo(row.done ? "Подход удалён" : "Строка удалена", () => {
      for (const saved of undoSaves(row, removal)) void saveSet(saved.setId, draftOf(item, saved));
      setLayouts((prev) => restoreKey(prev, key, before.layout));
      setWarmups((prev) => restoreKey(prev, key, before.warmup));
      setDrafts(before.drafts);
    });
  }

  function setRpe(rpe: number | null) {
    if (!rpeFor?.row.setId) return;
    haptic("light");
    void saveSet(rpeFor.row.setId, draftOf(rpeFor.item, { ...rpeFor.row, rpe }));
    setRpeFor(null);
  }

  function setRestPref(item: PlanItem, seconds: number) {
    haptic("light");
    const next = { ...restPrefs, [item.exercise.id]: seconds };
    setRestPrefs(next);
    setRestFor(null);
    try {
      localStorage.setItem(REST_KEY, JSON.stringify(next));
    } catch {
      /* не запомнилось — до конца экрана всё равно работает */
    }
  }

  async function onFinish() {
    // Из стора, а не из замыкания рендера: перед завершением могли только что
    // засчитать неотмеченные подходы, и в замыкании их ещё нет.
    const session = useSession.getState().active;
    if (!session || finishing) return;
    setFinishing(true);
    setStatsOpen(false);
    // Ничего не записано — нечего и завершать: тренировки без подходов не
    // существует (21-retention-plan.md §1). Раньше такие копились в истории,
    // слали тренеру «0 кг» и отмечали день плана сделанным.
    if (session.sets.length === 0) {
      await discard();
      onLeave();
      return;
    }
    // Считаем ДО finish(): он стирает active, а вместе с ним — подходы и план.
    const muscleOf = new Map(session.plan.map((p) => [p.exercise.id, p.exercise.primary_muscle]));
    const muscleCounts: Record<string, number> = {};
    for (const s of session.sets) {
      if (s.kind === "warmup") continue;
      const muscle = muscleOf.get(s.exercise_id);
      if (muscle) muscleCounts[muscle] = (muscleCounts[muscle] ?? 0) + 1;
    }
    const volumeKg = session.sets.reduce((sum, s) => sum + (s.weight_kg ?? 0) * (s.reps ?? 0), 0);
    const work = session.sets.filter((s) => s.kind !== "warmup");
    const durationSec = Math.max(0, Math.round((Date.now() - new Date(session.started_at).getTime()) / 1000));

    const taken = await finish();
    onFinished(
      {
        records: taken,
        muscleCounts,
        volumeKg,
        durationSec,
        sets: work.length,
        exercises: new Set(work.map((s) => s.exercise_id)).size,
      },
      // Тренировку уже стёрли из стора, а карточке в чат нужен её id —
      // поэтому он уезжает наверх вместе с фактами.
      session.client_id,
    );
  }

  async function finishWith(logPending: boolean) {
    setFinishCheck(false);
    if (logPending) {
      for (const { item, rows } of lists) {
        for (const row of pendingRows(rows)) await saveSet(null, { ...draftOf(item, row), rpe: null });
      }
    }
    await onFinish();
  }

  const syncLine =
    pending > 0
      ? `${plural(pending, "запись ждёт", "записи ждут", "записей ждут")} отправки — данные уже сохранены на устройстве`
      : `${plural(active.sets.length, "подход", "подхода", "подходов")} записано`;

  // Неотмеченные подходы — сначала спрашиваем, что с ними. Иначе завершение
  // вторым тапом: одна большая кнопка рядом с «+ Упражнение» — лёгкий промах.
  const finishControl = finishing ? (
    <button className="button button--wide" disabled>
      <BusyLabel text="Завершаю" />
    </button>
  ) : pendingCount > 0 ? (
      <button
        className="button button--wide"
        disabled={finishing}
        onClick={() => {
          haptic();
          setStatsOpen(false);
          setFinishCheck(true);
        }}
      >
        Завершить тренировку
      </button>
    ) : active.sets.length === 0 ? (
      // Нечего сохранять — и кнопка говорит это прямо, а не обещает итог.
      <ConfirmButton
        className="button button--wide"
        label="Выйти без сохранения"
        confirm="Ничего не записано — выйти?"
        disabled={finishing}
        onConfirm={onFinish}
      />
    ) : (
      <ConfirmButton
        className="button button--wide"
        label="Завершить тренировку"
        confirm="Нажми ещё раз — завершить"
        disabled={finishing}
        onConfirm={onFinish}
      />
    );

  const menuItem = menuFor;
  const menuLocked =
    menuItem !== null &&
    (menuItem.program_exercise_id === null || setsOf(menuItem, active.sets).length > 0);

  return (
    <div className="session">
      <header className="wo-bar">
        <button
          className="wo-bar__collapse"
          aria-label="Свернуть тренировку"
          onClick={() => {
            haptic("light");
            onLeave();
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 9.5l6 6 6-6" />
          </svg>
        </button>
        {/* Пока идёт отдых, в шапке — он: между подходами смотрят именно сюда,
            а длительность тренировки в эти две минуты никому не нужна. */}
        {restTimer.rest ? (
          <span className="wo-bar__clock wo-bar__clock--rest" aria-label="Отдых">
            <span className="wo-bar__clock-key">отдых</span>
            {fmtRest(restTimer.rest.left)}
          </span>
        ) : (
          <span className="wo-bar__clock" aria-label="Длительность">
            {clock(elapsed)}
          </span>
        )}
        <button
          className="wo-bar__tool"
          aria-label="Калькулятор: 1ПМ, рабочий вес, блины"
          onClick={() => {
            haptic("light");
            setCalculator(true);
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="5" y="3" width="14" height="18" rx="2.5" />
            <path d="M8.5 7.5h7M9 12h.01M12 12h.01M15 12h.01M9 16h.01M12 16h.01M15 16h.01" />
          </svg>
        </button>
        <button
          className="wo-bar__pill"
          onClick={() => {
            haptic("light");
            setStatsOpen(true);
          }}
        >
          Статистика
        </button>
        {workRows.length > 0 && (
          <div
            className="wo-bar__progress"
            style={{ width: `${(doneCount / workRows.length) * 100}%` }}
            aria-hidden="true"
          />
        )}
      </header>

      <h1 className="wo-title">{active.title}</h1>
      <p className="wo-meta">
        {workRows.length > 0
          ? `${doneCount} из ${plural(workRows.length, "подхода", "подходов", "подходов")}`
          : plural(active.sets.length, "подход", "подхода", "подходов")}
        {" · "}
        {Math.round(volume).toLocaleString("ru-RU")} кг
      </p>

      {error && <p className="session__error">{error}</p>}

      {notes.map((note) => (
        <div className="card" key={note.id}>
          <p className="card__title">
            {note.author_name}
            {note.exercise_id !== null &&
              ` · ${
                lists.find((l) => l.item.exercise.id === note.exercise_id)?.item.exercise
                  .name_ru ?? "разбор"
              }`}
          </p>
          <p className="lead" style={{ margin: 0 }}>
            {note.text}
          </p>
          <button
            className="wo-btn"
            style={{ width: "100%", marginTop: 10 }}
            onClick={() => void dismiss(note.id)}
          >
            Понятно
          </button>
        </div>
      ))}

      <div className="session__list">
        {groups.map((group, gi) => (
          <div key={gi} className={`exgroup ${group.length > 1 ? "exgroup--chained" : ""}`}>
            {group.map((item) => (
              <ExerciseCard
                key={keyOf(item)}
                item={item}
                rows={rowsByKey.get(keyOf(item)) ?? []}
                drafts={drafts}
                currentIndex={currentKey === keyOf(item) ? current!.index : null}
                justDone={justDone}
                recordIndex={recordRow(rowsByKey.get(keyOf(item)) ?? [], item.record_weight_kg)}
                onCopyPrev={(row) => copyPrevious(item, row)}
                restSec={restOf(item)}
                onToggle={(row) => toggleTyped(item, row)}
                onEdit={(row, field) => {
                  haptic("light");
                  openField(item, row, field);
                }}
                onRemove={(row) => removeRow(item, row)}
                onRpe={(row) => {
                  haptic("light");
                  setRpeFor({ item, row });
                }}
                onAddRow={(kind) => addRow(item, kind)}
                onMenu={() => {
                  haptic("light");
                  setMenuFor(item);
                }}
                onRest={() => {
                  haptic("light");
                  setRestFor(item);
                }}
              />
            ))}
          </div>
        ))}

        {active.plan.length === 0 && (
          <p className="lead session__free">
            Свободная тренировка: программа не выбрана, поэтому целей и
            рекомендаций здесь нет.
          </p>
        )}

        <div className="session__tail">
          {/* Без этого ради одного незапланированного движения приходилось
              завершать тренировку и лезть в конструктор программы. */}
          <button
            className="wo-btn"
            onClick={() => {
              haptic();
              setAdding(true);
            }}
          >
            + Упражнение
          </button>
          {finishControl}
          <div className="session__sync">
            {/* Столбцы бегут, только пока есть сеть: без неё очередь стоит, и
                бегущий лоадер обещал бы отправку, которой нет. */}
            {pending > 0 && navigator.onLine && <DotLoader variant="bars" />}
            {syncLine}
          </div>
        </div>
      </div>

      {restTimer.rest && (
        <RestTimer rest={restTimer.rest} onShift={restTimer.shift} onSkip={restTimer.stop} />
      )}

      {undo && (
        <UndoToast
          key={undo.id}
          text={undo.text}
          raised={restTimer.rest !== null}
          onUndo={() => {
            haptic("medium");
            undo.run();
            setUndo(null);
          }}
          onExpire={() => setUndo(null)}
        />
      )}

      {statsOpen && (
        <Sheet title="Статистика тренировки" onClose={() => setStatsOpen(false)}>
          <BrandBlock tone="blue" seed={5} eyebrow="Идёт" value={clock(elapsed)} caption={active.title} />
          <div className="tiles">
            <div className="tile">
              <span className="tile__value">{Math.round(volume).toLocaleString("ru-RU")}</span>
              <span className="tile__key">объём, кг</span>
            </div>
            <div className="tile">
              <span className="tile__value">{active.sets.length}</span>
              <span className="tile__key">
                {plural(active.sets.length, "подход", "подхода", "подходов").replace(/^\d+\s/, "")}
              </span>
            </div>
          </div>
          <p className="sheet__hint">{syncLine}</p>
          {finishControl}
        </Sheet>
      )}

      {finishCheck && (
        <Sheet
          title={plural(pendingCount, "подход не отмечен", "подхода не отмечены", "подходов не отмечены")}
          hint="В строках стоят подсказанные числа, но галочки нет — такие подходы не записаны."
          onClose={() => setFinishCheck(false)}
        >
          <button
            className="button button--wide"
            disabled={finishing}
            onClick={() => void finishWith(true)}
          >
            Засчитать как есть и завершить
          </button>
          <button className="wo-btn" disabled={finishing} onClick={() => void finishWith(false)}>
            Не записывать и завершить
          </button>
          <button className="menu__item menu__item--center" onClick={() => setFinishCheck(false)}>
            Вернуться к тренировке
          </button>
        </Sheet>
      )}

      {menuItem && (
        <Sheet title={menuItem.exercise.name_ru} onClose={() => setMenuFor(null)}>
          <div className="menu">
            {/* Подходы уже записаны: они сделаны этим движением, замена его
                исказит. Добавленное на ходу заменять незачем — его и выбрали
                только что. */}
            <button
              className="menu__item"
              disabled={menuLocked}
              onClick={() => {
                setMenuFor(null);
                setSwapping(menuItem);
              }}
            >
              Заменить упражнение
              {menuLocked && (
                <span className="menu__why">
                  {menuItem.program_exercise_id === null
                    ? "добавлено сегодня"
                    : "подходы уже записаны"}
                </span>
              )}
            </button>
            {!warmups[keyOf(menuItem)]?.length && warmupOf(menuItem).length > 0 && (
              <button
                className="menu__item"
                onClick={() => {
                  haptic();
                  setWarmups((prev) => ({ ...prev, [keyOf(menuItem)]: warmupOf(menuItem) }));
                  setMenuFor(null);
                }}
              >
                Добавить разминку
                <span className="menu__why">
                  {plural(warmupOf(menuItem).length, "ступень", "ступени", "ступеней")}
                </span>
              </button>
            )}
            <button
              className="menu__item"
              onClick={() => {
                setMenuFor(null);
                setRestFor(menuItem);
              }}
            >
              Таймер отдыха
              <span className="menu__why">{restLabel(restOf(menuItem))}</span>
            </button>
          </div>
        </Sheet>
      )}

      {restFor && (
        <Sheet
          title="Таймер отдыха"
          hint={`Запомнится для «${restFor.exercise.name_ru}».${
            restFor.rest_sec ? ` В программе — ${restLabel(restFor.rest_sec)}.` : ""
          }`}
          onClose={() => setRestFor(null)}
        >
          <div className="opts">
            {REST_OPTIONS.map((sec) => (
              <button
                key={sec}
                className={`opt ${restOf(restFor) === sec ? "opt--on" : ""}`}
                onClick={() => setRestPref(restFor, sec)}
              >
                <span className="opt__main">{restLabel(sec)}</span>
              </button>
            ))}
          </div>
        </Sheet>
      )}

      {rpeFor && (
        <Sheet
          title="Насколько тяжело"
          hint="RPE — сколько повторов ещё оставалось в запасе. 10 — отказ."
          onClose={() => setRpeFor(null)}
        >
          <div className="opts">
            {RPE_SCALE.map((value) => (
              <button
                key={value}
                className={`opt ${rpeFor.row.rpe === value ? "opt--on" : ""}`}
                onClick={() => setRpe(value)}
              >
                <span className="opt__main">{value}</span>
                <span className="opt__sub">{reserveLabel(value)}</span>
              </button>
            ))}
          </div>
          {rpeFor.row.rpe !== null && (
            <button className="wo-btn" onClick={() => setRpe(null)}>
              Убрать RPE
            </button>
          )}
        </Sheet>
      )}

      {swapping && (
        <ExercisePickSheet
          title={`Заменить «${swapping.exercise.name_ru}»`}
          hint="Цель останется от программы, вес пересчитается по новому упражнению."
          suggestions={swapping.alternatives.map((a) => a.exercise)}
          similarTo={swapping.exercise.id}
          exclude={[swapping.exercise.id]}
          onClose={() => setSwapping(null)}
          onPick={(exercise: Exercise) => {
            const item = swapping;
            setSwapping(null);
            void swap(item, exercise);
          }}
        />
      )}

      {adding && (
        <ExercisePickSheet
          title="Добавить упражнение"
          hint="Только на эту тренировку — программа не изменится."
          exclude={active.plan.map((p) => p.exercise.id)}
          onClose={() => setAdding(false)}
          onPick={(exercise: Exercise) => {
            setAdding(false);
            void addExtra(exercise);
          }}
        />
      )}

      {editing && (
        <Numpad
          // Новый ключ на каждое поле: иначе набранное для веса перетекло бы в повторы.
          key={`${keyOf(editing.item)}:${editing.row.index}:${editing.field}`}
          title={editing.field === "weight" ? "Вес, кг" : "Повторы"}
          value={editing.field === "weight" ? editing.row.weight : editing.row.reps}
          step={editing.field === "weight" ? 2.5 : 1}
          decimal={editing.field === "weight"}
          barKg={
            editing.field === "weight" ? barFor(editing.item.exercise.equipment) : null
          }
          submitLabel={
            editing.row.done
              ? "Готово"
              : editing.field === "weight"
                ? "Далее — повторы"
                : editing.chained
                  ? "Засчитать подход"
                  : "Готово"
          }
          onDraft={(value) => {
            typed.current = value;
          }}
          // Закрыли крестиком — набранное всё равно записываем: цифры набирали
          // не для того, чтобы они пропали. «Далее» остаётся для цепочки.
          onClose={() => {
            commitTyped();
            setEditing(null);
          }}
          onSubmit={applyValue}
        />
      )}

      {calculator && (
        <CalcSheet
          exercises={active.plan.map((p) => p.exercise)}
          onClose={() => setCalculator(false)}
        />
      )}
    </div>
  );
}

function ExerciseCard({
  item,
  rows,
  drafts,
  currentIndex,
  justDone,
  recordIndex,
  restSec,
  onToggle,
  onCopyPrev,
  onEdit,
  onRemove,
  onRpe,
  onAddRow,
  onMenu,
  onRest,
}: {
  item: PlanItem;
  rows: Row[];
  drafts: Record<string, Draft>;
  /** Индекс строки текущего подхода, если он в этом упражнении. */
  currentIndex: number | null;
  /** «пункт:строка» только что засчитанного подхода. */
  justDone: string | null;
  /** Строка, взявшая рекорд веса в этой тренировке. */
  recordIndex: number | null;
  restSec: number;
  onCopyPrev: (row: Row) => void;
  onToggle: (row: Row) => void;
  onEdit: (row: Row, field: "weight" | "reps") => void;
  onRemove: (row: Row) => void;
  onRpe: (row: Row) => void;
  onAddRow: (kind: SetKind) => void;
  onMenu: () => void;
  onRest: () => void;
}) {
  const rec = item.recommendation;
  let working = 0;

  return (
    <section className="wo-card">
      <div className="wo-card__head">
        <ExerciseIcon exercise={item.exercise} />
        <button className="wo-card__name" onClick={onMenu}>
          {item.exercise.name_ru}
          {item.is_backoff && <span className="wo-card__tag">добивка</span>}
          {item.is_swapped && <span className="wo-card__tag wo-card__tag--swap">замена</span>}
        </button>
        <button
          className="wo-card__more"
          aria-label={`Действия: ${item.exercise.name_ru}`}
          onClick={onMenu}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="5.5" r="1.7" />
            <circle cx="12" cy="12" r="1.7" />
            <circle cx="12" cy="18.5" r="1.7" />
          </svg>
        </button>
      </div>

      {item.notes && <p className="wo-card__note">{item.notes}</p>}

      {/* §2.2: вес без причины — недоделанная рекомендация, а не лаконичная. */}
      <p className="wo-card__rec">
        {rec.weight_kg !== null && <b>{fmtKg(rec.weight_kg)} кг</b>}
        {rec.weight_kg !== null && " · "}
        {rec.why}
      </p>

      <button className="wo-card__rest" onClick={onRest}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="13.5" r="7.5" />
          <path d="M12 13.5V10M10 3h4M18.5 6.5l1.2-1.2" />
        </svg>
        Таймер отдыха: <b className={restSec === 0 ? "is-off" : ""}>{restLabel(restSec)}</b>
      </button>

      <div className="wo-sets">
        <div className="wo-sets__head" aria-hidden="true">
          <span>Подх.</span>
          <span>Пред.</span>
          <span>Кг</span>
          <span>Повт.</span>
          <span>✓</span>
          <span />
        </div>

        {rows.map((row) => {
          if (row.kind === "working" || row.kind === "failure") working += 1;
          return (
            <SetRow
              key={row.index}
              row={row}
              draft={drafts[`${keyOf(item)}:${row.index}`]}
              current={row.index === currentIndex}
              just={justDone === `${keyOf(item)}:${row.index}`}
              record={row.index === recordIndex}
              onCopyPrev={() => onCopyPrev(row)}
              number={working}
              onToggle={() => onToggle(row)}
              onEdit={(field) => onEdit(row, field)}
              onRemove={() => onRemove(row)}
              onRpe={() => onRpe(row)}
            />
          );
        })}
      </div>

      {/* Подходов в зале выходит и больше, и меньше плана. Дропсет — отдельной
          кнопкой: сброс не рабочий подход, и в подбор веса он идти не должен. */}
      <div className="wo-card__add">
        <button className="wo-btn" onClick={() => onAddRow("working")}>
          + Подход
        </button>
        <button className="wo-btn wo-btn--drop" onClick={() => onAddRow("drop")}>
          + Дропсет
        </button>
      </div>
    </section>
  );
}

function SetRow({
  row,
  draft,
  current,
  just,
  record,
  number,
  onToggle,
  onCopyPrev,
  onEdit,
  onRemove,
  onRpe,
}: {
  row: Row;
  draft: Draft | undefined;
  current: boolean;
  just: boolean;
  record: boolean;
  number: number;
  onCopyPrev: () => void;
  onToggle: () => void;
  onEdit: (field: "weight" | "reps") => void;
  onRemove: () => void;
  onRpe: () => void;
}) {
  const label =
    row.kind === "warmup" ? "разминка" : row.kind === "drop" ? "сброс" : `подход ${number}`;

  const badge =
    row.kind === "warmup" ? (
      <span className="wo-set__no--w">W</span>
    ) : row.kind === "drop" ? (
      <span className="wo-set__no--d">D</span>
    ) : (
      number
    );

  const previous = row.previous?.weight_kg
    ? `${fmtKg(row.previous.weight_kg)} × ${row.previous.reps ?? "—"}`
    : "—";

  // Подсказанное число приглушено, пока подход не засчитан и число не
  // вводили руками: яркие цифры без галочки выглядели записанными, и забытый
  // подход пропадал молча.
  const inputClass = (value: number | null, typed: boolean) =>
    `wo-set__input ${
      value === null ? "wo-set__input--empty" : !row.done && !typed ? "wo-set__input--hint" : ""
    }`;

  return (
    <div
      className={`wo-set ${row.done ? "wo-set--done" : ""} ${current ? "wo-set--current" : ""} ${
        just ? "wo-set--just" : ""
      }`}
    >
      <span className="wo-set__no">{badge}</span>
      {!row.done && row.previous?.weight_kg ? (
        <button
          className="wo-set__prev wo-set__prev--copy"
          onClick={onCopyPrev}
          aria-label={`Как в прошлый раз: ${previous}`}
        >
          {previous}
        </button>
      ) : (
        <span className="wo-set__prev">{previous}</span>
      )}
      <button
        className={inputClass(row.weight, draft?.weight !== undefined)}
        onClick={() => onEdit("weight")}
        aria-label={`Вес, ${label}`}
      >
        {fmtKg(row.weight)}
      </button>
      <button
        className={inputClass(row.reps, draft?.reps !== undefined)}
        onClick={() => onEdit("reps")}
        aria-label={`Повторы, ${label}`}
      >
        {row.reps ?? ""}
      </button>
      <button
        className="wo-set__check"
        aria-pressed={row.done}
        aria-label={`Засчитать: ${label}`}
        onClick={onToggle}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      </button>
      <button className="wo-set__del" aria-label={`Удалить: ${label}`} onClick={onRemove}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
      {record && <span className="wo-set__pr">Рекорд веса</span>}
      {/* RPE спрашиваем только у сделанного: оценить тяжесть можно после
          подхода, а не до. */}
      {row.done && (
        <button
          className={`wo-set__rpe ${row.rpe !== null ? `wo-set__rpe--${rpeTone(row.rpe)}` : ""}`}
          onClick={onRpe}
        >
          {row.rpe === null ? "+ RPE" : `RPE ${row.rpe}`}
        </button>
      )}
    </div>
  );
}

/**
 * Каркас экрана тренировки.
 *
 * Показывается в зазоре, пока стор поднимает тренировку: форма экрана уже на
 * месте, и появление подходов не выглядит сменой экрана.
 */
export function SessionSkeleton() {
  return (
    <div className="session">
      <div className="wo-bar">
        <div className="skeleton skeleton--clock" />
      </div>
      <div className="session__list session__list--skeleton">
        <div className="skeleton skeleton--title" />
        <div className="skeleton skeleton--card" />
        <div className="skeleton skeleton--card" />
      </div>
    </div>
  );
}
